import { Fragment, useLayoutEffect, useRef, useState } from "react";

import styles from "@/components/AnimatedIntegrationHub.module.css";
import LumientLogoMark from "@/components/LumientLogoMark";

type Side = "left" | "right";
type SlotIndex = 0 | 1 | 2;
type IntegrationIcon =
  | "pdf"
  | "spreadsheet"
  | "docs"
  | "chat"
  | "crm"
  | "billing"
  | "ticketing"
  | "database"
  | "storage"
  | "calendar";

interface IconStyle {
  bg: string;
  color: string;
}

interface Integration {
  id: string;
  name: string;
  label: string;
  icon: IntegrationIcon;
}

interface VisibleNode {
  instanceId: string;
  integration: Integration;
  side: Side;
  slotIndex: SlotIndex;
}

interface ConnectorParticleFlow {
  delayMs: number;
  durationMs: number;
  radius: number;
}

interface AnimatedIntegrationHubProps {
  iconStyle?: IconStyle;
  idleDurationMs?: number;
  retireDurationMs?: number;
  shiftDurationMs?: number;
  insertDurationMs?: number;
  random?: () => number;
}

const DEFAULT_ICON_STYLE = { bg: "oklch(0.95 0.004 75)", color: "oklch(0.38 0.012 70)" };

const VIEWBOX_WIDTH = 580;
const VIEWBOX_HEIGHT = 380;
const HUB_CENTER = { x: 290, y: 188 };
const HUB_HALF_WIDTH = 43;
const HUB_LEFT_EDGE = { x: HUB_CENTER.x - HUB_HALF_WIDTH, y: HUB_CENTER.y };
const HUB_RIGHT_EDGE = { x: HUB_CENTER.x + HUB_HALF_WIDTH, y: HUB_CENTER.y };
const NODE_CARD_WIDTH = 116;
const NODE_CARD_HEIGHT = 54;

const SLOT_COORDS: Record<Side, Record<SlotIndex, { x: number; y: number }>> = {
  left: {
    0: { x: 116, y: 44 },
    1: { x: 40, y: 188 },
    2: { x: 116, y: 332 },
  },
  right: {
    0: { x: 464, y: 44 },
    1: { x: 540, y: 188 },
    2: { x: 464, y: 332 },
  },
};

const CONNECTOR_PARTICLE_FLOWS: Record<Side, Record<SlotIndex, ConnectorParticleFlow[]>> = {
  left: {
    0: [
      { delayMs: -620, durationMs: 3600, radius: 3.3 },
      { delayMs: -2140, durationMs: 5200, radius: 2.45 },
    ],
    1: [
      { delayMs: -1280, durationMs: 4450, radius: 3.1 },
      { delayMs: -2920, durationMs: 6100, radius: 2.3 },
    ],
    2: [
      { delayMs: -360, durationMs: 3920, radius: 3.25 },
      { delayMs: -2460, durationMs: 5480, radius: 2.4 },
    ],
  },
  right: {
    0: [
      { delayMs: -1740, durationMs: 4750, radius: 3.15 },
      { delayMs: -310, durationMs: 5850, radius: 2.35 },
    ],
    1: [
      { delayMs: -940, durationMs: 4080, radius: 3.35 },
      { delayMs: -3380, durationMs: 6420, radius: 2.45 },
    ],
    2: [
      { delayMs: -2260, durationMs: 5050, radius: 3.05 },
      { delayMs: -720, durationMs: 5660, radius: 2.25 },
    ],
  },
};

const INITIAL_NODES: VisibleNode[] = [
  {
    instanceId: "integration-node-files",
    integration: { id: "files", name: "Files", label: "Files", icon: "storage" },
    side: "left",
    slotIndex: 0,
  },
  {
    instanceId: "integration-node-docs",
    integration: { id: "docs", name: "Docs", label: "Docs", icon: "docs" },
    side: "left",
    slotIndex: 1,
  },
  {
    instanceId: "integration-node-spreadsheet",
    integration: { id: "spreadsheet", name: "Spreadsheet", label: "Sheets", icon: "spreadsheet" },
    side: "left",
    slotIndex: 2,
  },
  {
    instanceId: "integration-node-crm",
    integration: { id: "crm", name: "CRM", label: "CRM", icon: "crm" },
    side: "right",
    slotIndex: 0,
  },
  {
    instanceId: "integration-node-billing",
    integration: { id: "billing", name: "Billing", label: "Billing", icon: "billing" },
    side: "right",
    slotIndex: 1,
  },
  {
    instanceId: "integration-node-database",
    integration: { id: "database", name: "Database", label: "Database", icon: "database" },
    side: "right",
    slotIndex: 2,
  },
];

function sortNodes(nodes: VisibleNode[]) {
  return [...nodes].sort((a, b) => {
    if (a.side === b.side) {
      return a.slotIndex - b.slotIndex;
    }

    return a.side === "left" ? -1 : 1;
  });
}

function sourceIconProps() {
  return {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24" as const,
    fill: "none" as const,
    stroke: "currentColor" as const,
    strokeWidth: "1.9",
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
}

function SourceIcon({ type }: { type: IntegrationIcon }) {
  const base = sourceIconProps();

  switch (type) {
    case "pdf":
      return (
        <svg {...base}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <path d="M9 13h1.5a1 1 0 0 1 0 2H9v2" />
          <path d="M14 13h2" />
          <line x1="14" y1="13" x2="14" y2="17" />
        </svg>
      );

    case "spreadsheet":
      return (
        <svg {...base}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M3 9h18M9 3v18M15 3v18M3 15h18" />
        </svg>
      );

    case "docs":
      return (
        <svg {...base}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="8" y1="13" x2="16" y2="13" />
          <line x1="8" y1="17" x2="13" y2="17" />
        </svg>
      );

    case "chat":
      return (
        <svg {...base}>
          <path d="M4 5h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9l-5 4v-4H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z" />
        </svg>
      );

    case "crm":
      return (
        <svg {...base}>
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <circle cx="9" cy="10" r="2.4" />
          <path d="M6.6 16c.6-1.6 2-2.6 3.4-2.6 1.4 0 2.8 1 3.4 2.6" />
          <line x1="14.5" y1="9" x2="18" y2="9" />
          <line x1="14.5" y1="13" x2="18" y2="13" />
        </svg>
      );

    case "billing":
      return (
        <svg {...base}>
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <line x1="2" y1="10" x2="22" y2="10" />
          <line x1="6" y1="15" x2="10" y2="15" />
          <line x1="14" y1="15" x2="18" y2="15" />
        </svg>
      );

    case "ticketing":
      return (
        <svg {...base}>
          <path d="M4 8a2 2 0 0 0 0 4v4h16v-4a2 2 0 1 0 0-4V4H4Z" />
          <path d="M12 4v12" strokeDasharray="2 2" />
        </svg>
      );

    case "database":
      return (
        <svg {...base}>
          <ellipse cx="12" cy="5" rx="9" ry="3" />
          <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
          <line x1="3" y1="12" x2="21" y2="12" />
        </svg>
      );

    case "storage":
      return (
        <svg {...base}>
          <path d="M6 18a6 6 0 0 1 .6-12A7 7 0 0 1 20 8.5 4.5 4.5 0 0 1 19 18Z" />
        </svg>
      );

    case "calendar":
      return (
        <svg {...base}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <line x1="3" y1="10" x2="21" y2="10" />
          <line x1="8" y1="3" x2="8" y2="7" />
          <line x1="16" y1="3" x2="16" y2="7" />
        </svg>
      );
  }
}

function lineStartForSide(side: Side) {
  return side === "left" ? HUB_LEFT_EDGE : HUB_RIGHT_EDGE;
}

function lineEndForNode(node: VisibleNode) {
  return SLOT_COORDS[node.side][node.slotIndex];
}

function timingValue(ms: number) {
  return `${ms}ms`;
}

export default function AnimatedIntegrationHub({ iconStyle = DEFAULT_ICON_STYLE }: AnimatedIntegrationHubProps) {
  const hubRef = useRef<HTMLDivElement>(null);
  const [hubScale, setHubScale] = useState(1);

  useLayoutEffect(() => {
    const hub = hubRef.current;

    if (!hub) {
      return;
    }

    const updateHubScale = () => {
      const width = hub.clientWidth;

      if (width > 0) {
        setHubScale(Math.min(1, width / VIEWBOX_WIDTH));
      }
    };

    updateHubScale();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateHubScale);

      return () => {
        window.removeEventListener("resize", updateHubScale);
      };
    }

    const observer = new ResizeObserver(updateHubScale);
    observer.observe(hub);

    return () => {
      observer.disconnect();
    };
  }, []);

  const sortedNodes = sortNodes(INITIAL_NODES);

  return (
    <div
      ref={hubRef}
      className="relative mx-auto w-full max-w-[580px]"
      style={{ height: VIEWBOX_HEIGHT * hubScale }}
      data-testid="animated-integration-hub"
    >
      <div
        className="absolute left-1/2 top-0 h-[380px] w-[580px] origin-top"
        style={{ transform: `translateX(-50%) scale(${hubScale})` }}
        data-testid="animated-integration-hub-frame"
      >
        <svg
          className="absolute inset-0 h-full w-full overflow-visible"
          aria-hidden="true"
          viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
        >
          {sortedNodes.map((node, index) => {
            const start = lineStartForSide(node.side);
            const end = lineEndForNode(node);

            return (
              <Fragment key={node.instanceId}>
                <line
                  className={styles.connectorLine}
                  data-testid={`integration-line-${node.side}-${node.slotIndex}`}
                  x1={start.x}
                  y1={start.y}
                  x2={end.x}
                  y2={end.y}
                  style={{ animationDelay: `${index * 0.36}s` }}
                  strokeLinecap="round"
                />
                {CONNECTOR_PARTICLE_FLOWS[node.side][node.slotIndex].map((particle, particleIndex) => {
                  const dur = timingValue(particle.durationMs);
                  const begin = timingValue(particle.delayMs);
                  const endRadius = Math.max(0.9, particle.radius * 0.34).toFixed(2);

                  return (
                    <circle
                      key={`${node.instanceId}-particle-${particleIndex}`}
                      className={styles.connectorParticle}
                      data-testid={`integration-particle-${node.side}-${node.slotIndex}-${particleIndex}`}
                      cx={end.x}
                      cy={end.y}
                      r={particle.radius}
                      opacity="0"
                    >
                      <animate
                        attributeName="cx"
                        values={`${end.x};${start.x}`}
                        dur={dur}
                        begin={begin}
                        repeatCount="indefinite"
                        calcMode="spline"
                        keyTimes="0;1"
                        keySplines="0.35 0 0.2 1"
                      />
                      <animate
                        attributeName="cy"
                        values={`${end.y};${start.y}`}
                        dur={dur}
                        begin={begin}
                        repeatCount="indefinite"
                        calcMode="spline"
                        keyTimes="0;1"
                        keySplines="0.35 0 0.2 1"
                      />
                      <animate
                        attributeName="opacity"
                        values="0;0.94;0.72;0"
                        keyTimes="0;0.18;0.76;1"
                        dur={dur}
                        begin={begin}
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="r"
                        values={`${particle.radius};${(particle.radius * 0.68).toFixed(2)};${endRadius}`}
                        keyTimes="0;0.52;1"
                        dur={dur}
                        begin={begin}
                        repeatCount="indefinite"
                      />
                    </circle>
                  );
                })}
              </Fragment>
            );
          })}
        </svg>

        <div
          className="absolute z-20"
          style={{
            left: HUB_CENTER.x - 43,
            top: HUB_CENTER.y - 43,
          }}
        >
          <div className="rounded-[18px] border border-border bg-surface-panel p-1.5 shadow-[0_4px_24px_oklch(0.12_0.008_75_/_0.10)]">
            <div className="flex h-[78px] w-[78px] items-center justify-center rounded-[20px] bg-surface-panel">
              <LumientLogoMark className="h-10 w-10 text-primary" />
            </div>
          </div>
        </div>

        {sortedNodes.map((node) => {
          const position = SLOT_COORDS[node.side][node.slotIndex];
          return (
            <div
              key={node.instanceId}
              className={`${styles.nodeShell} absolute z-10 rounded-[18px] border border-border bg-surface-panel px-3 py-2 shadow-[0_3px_12px_oklch(0.12_0.008_75_/_0.07)]`}
              style={{
                left: 0,
                top: 0,
                width: NODE_CARD_WIDTH,
                height: NODE_CARD_HEIGHT,
                transform: `translate3d(${position.x - NODE_CARD_WIDTH / 2}px, ${
                  position.y - NODE_CARD_HEIGHT / 2
                }px, 0)`,
              }}
              data-testid="integration-node"
            >
              <div className={styles.nodeContent}>
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px]"
                  role="img"
                  aria-label={node.integration.name}
                  style={{ background: iconStyle.bg, color: iconStyle.color }}
                >
                  <SourceIcon type={node.integration.icon} />
                </div>
                <span className="whitespace-nowrap text-[10px] font-medium leading-none tracking-[0.01em] text-[oklch(0.32_0.01_70)]">
                  {node.integration.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
